import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import { Box, Divider, Grid } from '@mui/material';

import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Dropdown from '@/components/Dropdown/Dropdown';
import Input from '@/components/Input/Input';

import styles from './FZStoreModifierFilter.module.css';
import { useTranslation } from 'react-i18next';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import {
  flattenModifiersFromSlots,
  getDefaultModifierIdsForCategory,
  getDefaultSubcategorySlotId,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import { FilteredModifierValues } from '@/types/menus.types';

export type OtherCategoryFilterSlot = {
  value: string;
  label: string;
  modifiers: { modifierId: string; modifierName: string }[];
};

type FZStoreMenuFilterProps = {
  categories: {
    value: string;
    label: string;
  }[];
  isLoadingCategories: boolean;
  categoryName: string;
  /** Category to select when the user clears filters (typically first API category). */
  defaultCategoryName: string;
  /** When true, show modifier multi-select instead of modifier text search. */
  showModifierMultiSelect: boolean;
  /** When true, show subcategory dropdown (Other only). */
  showSubcategoryFilter: boolean;
  /** Sorted slots (and per-slot modifiers) for the active multi-select category; empty if none. */
  otherCategoryFilterSlots: OtherCategoryFilterSlot[];
  onApplyFilter: (filteredValues: FilteredModifierValues) => void;
  onCategoryChange: (newCategory: string) => void;
  onClearFilter: () => void;
  searching: boolean;
};

const FZStoreModifierFilter: FC<FZStoreMenuFilterProps> = ({
  categories,
  categoryName,
  defaultCategoryName,
  onCategoryChange,
  showModifierMultiSelect,
  showSubcategoryFilter,
  otherCategoryFilterSlots,
  isLoadingCategories,
  onApplyFilter,
  onClearFilter,
  searching,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [option1, setOption1] = useState('');
  const [option2, setOption2] = useState('');
  const [otherSlotId, setOtherSlotId] = useState('');
  const [otherSelectedModifierIds, setOtherSelectedModifierIds] = useState<string[]>([]);

  const modifiersForMultiSelect = useMemo(() => {
    if (!showModifierMultiSelect || !otherCategoryFilterSlots.length) return [];

    if (showSubcategoryFilter) {
      return otherCategoryFilterSlots.find((s) => s.value === otherSlotId)?.modifiers ?? [];
    }

    return flattenModifiersFromSlots(otherCategoryFilterSlots);
  }, [showModifierMultiSelect, showSubcategoryFilter, otherCategoryFilterSlots, otherSlotId]);

  const modifierDropdownOptions = useMemo(
    () => modifiersForMultiSelect.map((m) => ({ value: m.modifierId, label: m.modifierName })),
    [modifiersForMultiSelect],
  );

  useLayoutEffect(() => {
    if (!showSubcategoryFilter || !otherCategoryFilterSlots.length) {
      setOtherSlotId('');
      return;
    }
    setOtherSlotId((prev) =>
      otherCategoryFilterSlots.some((s) => s.value === prev) ? prev : otherCategoryFilterSlots[0].value,
    );
  }, [showSubcategoryFilter, otherCategoryFilterSlots]);

  useLayoutEffect(() => {
    if (!showModifierMultiSelect) {
      setOtherSelectedModifierIds([]);
      return;
    }
    if (!otherCategoryFilterSlots.length) {
      setOtherSlotId('');
      setOtherSelectedModifierIds([]);
      return;
    }
    if (!modifiersForMultiSelect.length) {
      setOtherSelectedModifierIds([]);
      return;
    }
    setOtherSelectedModifierIds((prev) => {
      const kept = prev.filter((id) => modifiersForMultiSelect.some((m) => m.modifierId === id));
      if (kept.length > 0) return kept;
      const subCategoryId = showSubcategoryFilter
        ? otherSlotId || getDefaultSubcategorySlotId(otherCategoryFilterSlots)
        : undefined;
      return getDefaultModifierIdsForCategory(otherCategoryFilterSlots, subCategoryId);
    });
  }, [showModifierMultiSelect, showSubcategoryFilter, otherCategoryFilterSlots, otherSlotId, modifiersForMultiSelect]);

  const handleOtherModifiersChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
    if (selected.length === 0 && modifiersForMultiSelect[0]) {
      setOtherSelectedModifierIds([modifiersForMultiSelect[0].modifierId]);
      return;
    }
    setOtherSelectedModifierIds(selected);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPrice(value);
  };
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setName(value);
  };

  const handleOption1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setOption1(value);
  };
  const handleOption2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setOption2(value);
  };
  const handleCategory = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setName('');
    setPrice('');
    setOption1('');
    setOption2('');
    onCategoryChange(value);
  };

  const slotDropdownOptions = useMemo(
    () => otherCategoryFilterSlots.map((s) => ({ value: s.value, label: s.label })),
    [otherCategoryFilterSlots],
  );

  const priceOptionFields = (
    <>
      <Grid item className={styles.otherSearchField}>
        <MaskedInput
          id="price"
          label={t('common.price')}
          prefix="$"
          fixedDecimalScale
          decimalScale={2}
          value={price}
          onChange={handlePriceChange}
        />
      </Grid>
      <Grid item className={styles.otherSearchField}>
        <Input
          label={t('common.option1')}
          value={option1}
          id="option1"
          subText={t('common.helpTextOption1Hint')}
          onChange={(e) => handleOption1(e)}
          subTextClassName={styles.subtextOverride}
        />
      </Grid>
      <Grid item className={styles.otherSearchField}>
        <Input label={t('common.option2')} value={option2} id="option2" onChange={(e) => handleOption2(e)} />
      </Grid>
    </>
  );

  return (
    <Box
      boxShadow="grey"
      mb={4}
      borderColor="red"
      borderRadius="10px"
      sx={{
        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
        width: { xs: '96%', sm: '96%', md: '97%', lg: '97%' },
      }}
    >
      {showModifierMultiSelect ? (
        <Grid
          container
          direction="column"
          alignItems="flex-start"
          p={3}
          className={styles.slotFilterLayout}
          style={{ maxWidth: '100vw' }}
        >
          <Grid container spacing={2} alignItems="flex-start" className={styles.categorySubcategoryRow}>
            <Grid item className={styles.categoryField}>
              <Dropdown
                label={t('common.category')}
                id="category"
                options={categories}
                loading={isLoadingCategories}
                hidePlaceholder={false}
                value={categoryName}
                onChange={(e) => handleCategory(e)}
                className={styles.categoryDropdown}
              />
            </Grid>
            {showSubcategoryFilter && slotDropdownOptions.length > 0 && (
              <Grid item className={styles.subcategoryField}>
                <Dropdown
                  label={t('common.subcategory')}
                  id="subcategory"
                  options={slotDropdownOptions}
                  loading={isLoadingCategories}
                  hidePlaceholder
                  value={otherSlotId}
                  onChange={(e) => setOtherSlotId(e.target.value)}
                  className={styles.subcategoryDropdown}
                />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} alignItems="flex-start" className={styles.modifierSearchRow}>
            {modifierDropdownOptions.length > 0 && (
              <Grid item className={styles.modifierMultiSelectField}>
                <label htmlFor="other-modifier-multi" className={styles.modifierMultiSelectLabel}>
                  {t('common.modifier')}
                </label>
                <select
                  id="other-modifier-multi"
                  className={styles.modifierMultiSelect}
                  multiple
                  size={Math.min(6, Math.max(3, modifierDropdownOptions.length))}
                  value={otherSelectedModifierIds}
                  onChange={handleOtherModifiersChange}
                  aria-label={t('common.modifier')}
                  disabled={isLoadingCategories}
                >
                  {modifierDropdownOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className={styles.modifierMultiSelectHint}>{t('common.helpTextModifierMultiSelect')}</div>
              </Grid>
            )}
            {priceOptionFields}
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="row" p={3} style={{ maxWidth: '100vw' }}>
          <Grid item paddingRight={3}>
            <Grid item minWidth="165px">
              <Dropdown
                label={t('common.category')}
                id="category"
                options={categories}
                loading={isLoadingCategories}
                hidePlaceholder={false}
                value={categoryName}
                onChange={(e) => handleCategory(e)}
                className={styles.dropdownSize}
              />
            </Grid>
          </Grid>
          <Divider orientation="vertical" variant="middle" flexItem />
          <Grid item paddingLeft={3} xs={5} sm={7} md={8} lg={7} xl={8}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={6} sm={6} md={3}>
                <Input
                  label={t('common.modifier')}
                  value={name}
                  id="modifier"
                  onChange={(e) => handleDescriptionChange(e)}
                  subText={t('common.helpTextModifierHint')}
                  subTextClassName={styles.subtextOverride}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <MaskedInput
                  id="price"
                  label={t('common.price')}
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                  value={price}
                  onChange={handlePriceChange}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Input
                  label={t('common.option1')}
                  value={option1}
                  id="option1"
                  subText={t('common.helpTextSizeCrustFlavor')}
                  onChange={(e) => handleOption1(e)}
                  subTextClassName={styles.subtextOverride}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Input label={t('common.option2')} value={option2} id="option2" onChange={(e) => handleOption2(e)} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Box p={2} sx={{ backgroundColor: 'var(--black-100)' }}>
        <Grid container direction="row">
          <Grid item>
            <Button
              variant={ButtonVariants.Secondary}
              isLoading={searching}
              onClick={() =>
                onApplyFilter({
                  modifierName: name,
                  subCategory: showSubcategoryFilter
                    ? otherSlotId || getDefaultSubcategorySlotId(otherCategoryFilterSlots)
                    : '',
                  modifierIds: showModifierMultiSelect
                    ? otherSelectedModifierIds.length > 0
                      ? otherSelectedModifierIds
                      : getDefaultModifierIdsForCategory(
                          otherCategoryFilterSlots,
                          showSubcategoryFilter
                            ? otherSlotId || getDefaultSubcategorySlotId(otherCategoryFilterSlots)
                            : undefined,
                        )
                    : undefined,
                  price,
                  option1,
                  option2,
                })
              }
            >
              {t('common.searchModifiers')}
            </Button>
          </Grid>
          <Grid item marginTop={1} ml={2}>
            <Button
              variant={ButtonVariants.Text}
              onClick={(e) => {
                onCategoryChange(defaultCategoryName);
                setName('');
                setPrice('');
                setOption1('');
                setOption2('');
                onClearFilter();
              }}
            >
              {t('common.clearFiltersButton')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FZStoreModifierFilter;
